import { cloneDeep, isEqual } from "es-toolkit";

export const MAX_UNDO_HISTORY_ENTRIES = 50;

export interface UndoHistoryState<TSnapshot> {
  entries: TSnapshot[];
  index: number;
}

export type UndoHistoryAction<TSnapshot> =
  | { snapshot: TSnapshot | null; type: "commitGesture" }
  | { snapshot: TSnapshot; type: "redo" }
  | { snapshot: TSnapshot; type: "seed" }
  | { snapshot: TSnapshot; type: "syncCheckpoint" }
  | { snapshot: TSnapshot; type: "undo" };

export type UndoHistoryReducer<TSnapshot> = (
  state: UndoHistoryState<TSnapshot> | null,
  action: UndoHistoryAction<TSnapshot>,
) => UndoHistoryState<TSnapshot> | null;

interface CreateUndoHistoryReducerOptions<TSnapshot> {
  isCommitEqual?: (baseline: TSnapshot, next: TSnapshot) => boolean;
  maxEntries?: number;
}

export const canUndoHistory = <TSnapshot>(
  state: UndoHistoryState<TSnapshot> | null,
): boolean => state != null && state.index > 0;

export const canRedoHistory = <TSnapshot>(
  state: UndoHistoryState<TSnapshot> | null,
): boolean => state != null && state.index < state.entries.length - 1;

export const createUndoHistoryReducer = <TSnapshot>(
  options: CreateUndoHistoryReducerOptions<TSnapshot> = {},
): UndoHistoryReducer<TSnapshot> => {
  const isCommitEqual = options.isCommitEqual ?? isEqual;
  const maxEntries = options.maxEntries ?? MAX_UNDO_HISTORY_ENTRIES;

  const toSnapshot = (value: TSnapshot): TSnapshot => cloneDeep(value);

  const pushPresent = (
    state: UndoHistoryState<TSnapshot>,
    present: TSnapshot,
  ): UndoHistoryState<TSnapshot> => {
    const entries = [...state.entries.slice(0, state.index + 1), present].slice(
      -maxEntries,
    );

    return {
      entries,
      index: entries.length - 1,
    };
  };

  const moveHistoryIndex = (
    state: UndoHistoryState<TSnapshot>,
    current: TSnapshot,
    nextIndex: number,
  ): UndoHistoryState<TSnapshot> => {
    const entries = [...state.entries];
    entries[state.index] = toSnapshot(current);
    return { entries, index: nextIndex };
  };

  return (state, action) => {
    switch (action.type) {
      case "seed":
        return {
          entries: [toSnapshot(action.snapshot)],
          index: 0,
        };

      case "syncCheckpoint": {
        if (state == null) {
          return state;
        }

        const entries = [...state.entries];
        entries[state.index] = toSnapshot(action.snapshot);
        return { entries, index: state.index };
      }

      case "commitGesture": {
        if (state == null || action.snapshot == null) {
          return state;
        }

        const baseline = state.entries[state.index];
        if (baseline == null || isCommitEqual(baseline, action.snapshot)) {
          return state;
        }

        return pushPresent(state, toSnapshot(action.snapshot));
      }

      case "undo": {
        if (state == null || !canUndoHistory(state)) {
          return state;
        }

        return moveHistoryIndex(state, action.snapshot, state.index - 1);
      }

      case "redo": {
        if (state == null || !canRedoHistory(state)) {
          return state;
        }

        return moveHistoryIndex(state, action.snapshot, state.index + 1);
      }
    }
  };
};
